import { extendTheme } from '@chakra-ui/react';

/**
 * Majalisna Design System - Theme Configuration
 * 
 * This is the single source of truth for all styling in the application.
 * All theme values (colors, components, styles, etc.) are defined here.
 * 
 * Key Features:
 * - RTL support with configurable direction
 * - Responsive design using Chakra's responsive props
 * - Consistent theming across all components
 * - Light/Dark mode support (currently light mode only)
 * 
 * Usage in components:
 * 1. Use Chakra UI components with style props
 * 2. For custom styles, use the theme object: theme.colors, theme.space, etc.
 * 3. For responsive values, use array syntax: [base, sm, md, lg, xl]
 * 4. For RTL support, use logical properties (start/end instead of left/right)
 */

// Majalisna color palette based on the provided brand colors
// Application configuration
const config = {
  app: {
    direction: 'rtl', // RTL for Arabic
  },
};

// Direction setting
const direction = config.app.direction || 'rtl';

// Color palette
const colors = {
  // Primary Colors
  // Brown - PANTONE P 18-6 C (RAL 8001)
  brown: {
    50: '#f5f2ed',
    100: '#e6e0d6',
    200: '#d6cbb9',
    300: '#c4b59c',
    400: '#b3a080',
    500: '#9d8a69', // Main brown color - HEX 9d 7d 69
    600: '#8a7057',
    700: '#725c48',
    800: '#5a4837',
    900: '#423426',
  },
  // Teal - PANTONE P 132-12 C (RAL 5201)
  teal: {
    50: '#edf6f4',
    100: '#d2e8e3',
    200: '#b0d5cd',
    300: '#8dc2b7',
    400: '#6fb0a1',
    500: '#57938a', // Main teal color - HEX 57 93 8a
    600: '#477a73',
    700: '#38615c',
    800: '#294945',
    900: '#1a302e',
  },
  // Beige - PANTONE P 13-4 C (RAL 1014)
  beige: {
    50: '#f9f6f1',
    100: '#f0ebe0',
    200: '#e5dac9',
    300: '#d9c9b2',
    400: '#ccba9b',
    500: '#c0aa85', // Main beige color - HEX c0 ba a1
    600: '#b09a75',
    700: '#9c8662',
    800: '#7a684d',
    900: '#594c38',
  },
  // Light Mint - PANTONE P 141-10 C (RAL 75001)
  mint: {
    50: '#f1f7f5',
    100: '#dfeae6',
    200: '#c7dcd5',
    300: '#aecdc4',
    400: '#96bfb3',
    500: '#7db0a2', // Main mint color - HEX ca da cd
    600: '#659e8e',
    700: '#4d7d70',
    800: '#355c52',
    900: '#1d3b34',
  },
  // Light Beige - PANTONE P 13-2 C (RAL 1104)
  lightBeige: {
    50: '#fbf9f4',
    100: '#f5f0e5',
    200: '#ede5d1',
    300: '#e5d9bd',
    400: '#ddcea9',
    500: '#d5c295', // Main light beige color - HEX dd d2 c0
    600: '#c4ad7c',
    700: '#ad9562',
    800: '#867347',
    900: '#5f522d',
  },
  // Secondary Colors
  // Navy Blue - PANTONE P 132-12 C (RAL 5201)
  navy: {
    50: '#eceef3',
    100: '#cfd3e0',
    200: '#afb6cc',
    300: '#8e99b7',
    400: '#6e7ca3',
    500: '#12233f', // Main navy color - HEX 12 23 3f
    600: '#0e1c32',
    700: '#0b1526',
    800: '#070e19',
    900: '#04070d',
  },
  // Burgundy - PANTONE P 13-4 C (RAL 1014)
  burgundy: {
    50: '#f2eaed',
    100: '#deccce',
    200: '#c8aaae',
    300: '#b1888d',
    400: '#9b666d',
    500: '#3c1c27', // Main burgundy color - HEX 3c 1c 27
    600: '#30161f',
    700: '#251018',
    800: '#190b10',
    900: '#0d0508',
  },
  // Light Teal - PANTONE P 141-10 C (RAL 75001)
  lightTeal: {
    50: '#edf4f4',
    100: '#d2e4e4',
    200: '#b4d2d2',
    300: '#95c0c0',
    400: '#77aeae',
    500: '#cad8d4', // Main light teal color - HEX ca d8 d4
    600: '#4c8e8e',
    700: '#3d7171',
    800: '#2e5555',
    900: '#1f3838',
  },
  // Light Lavender - PANTONE P 13-2 C (RAL 1104)
  lavender: {
    50: '#f4f3f7',
    100: '#e3e0ea',
    200: '#d1ccdd',
    300: '#beb8cf',
    400: '#aca4c2',
    500: '#ddd2c0', // Main lavender color - HEX dd d2 c0
    600: '#8a7da7',
    700: '#6e6486',
    800: '#534b64',
    900: '#373243',
  },
  // Jet Black - Pantone Black 6 C (RAL 9005)
  jetBlack: {
    50: '#e6e6e6',
    100: '#b3b3b3',
    200: '#808080',
    300: '#4d4d4d',
    400: '#1a1a1a',
    500: '#000000', // Main jet black color - HEX 00 00 00
    600: '#000000',
    700: '#000000',
    800: '#000000',
    900: '#000000',
  },
  success: {
    500: '#38A169',
  },
  warning: {
    500: '#DD6B20',
  },
  error: {
    500: '#E53E3E',
  },
  info: {
    500: '#3182CE',
  },
  gray: {
    50: '#F7FAFC',
    100: '#EDF2F7',
    200: '#E2E8F0',
    300: '#CBD5E0',
    400: '#A0AEC0',
    500: '#718096',
    600: '#4A5568',
    700: '#2D3748',
    800: '#1A202C',
    900: '#171923',
  },
};

// Font configuration for Arabic
const fonts = {
  heading: '"Tajawal", sans-serif',
  body: '"Tajawal", sans-serif',
};

// Component style overrides
const components = {
  Button: {
    baseStyle: {
      fontWeight: '500',
      borderRadius: 'md',
      transition: 'all 0.2s ease-in-out',
    },
    variants: {
      solid: {
        bg: 'brown.500',
        color: 'white',
        _hover: {
          bg: 'brown.600',
          transform: 'translateY(-2px)',
          boxShadow: 'md',
        },
        _active: {
          bg: 'brown.700',
          transform: 'translateY(0)',
        },
      },
      outline: {
        borderColor: 'teal.500',
        color: 'teal.500',
        _hover: {
          bg: 'teal.50',
          borderColor: 'teal.600',
          color: 'teal.600',
          transform: 'translateY(-2px)',
          boxShadow: 'md',
        },
        _active: {
          bg: 'teal.100',
          transform: 'translateY(0)',
        },
      },
    },
    // Add specific color variants
    defaultProps: {
      colorScheme: 'brown',
    },
  },
  Heading: {
    baseStyle: {
      fontWeight: '700',
      color: 'brown.800',
    },
  },
  Card: {
    baseStyle: {
      container: {
        borderRadius: 'lg',
        boxShadow: 'md',
      },
    },
  },
  Badge: {
    baseStyle: {
      borderRadius: 'full',
      px: 2,
      py: 1,
    },
  },
};

// Global styles
// Global styles are now defined in the theme extension below

// Create the theme with all configurations
const theme = extendTheme({
  // Core theme settings
  direction,
  colors,
  fonts,
  components,
  
  // RTL and theming configuration
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
    cssVarPrefix: 'majalisna',
  },
  
  // Global styles with RTL support
  styles: {
    global: (props) => ({
      body: {
        bg: 'gray.50',
        color: 'gray.800',
        fontFamily: 'body',
        direction: direction,
      },
      '*::placeholder': {
        color: 'gray.400',
      },
      '*, *::before, &::after': {
        borderColor: 'gray.200',
      },
      'html, body': {
        textAlign: direction === 'rtl' ? 'right' : 'left',
      },
    }),
  },
  
  // Text styles with RTL support
  textStyles: {
    h1: {
      fontSize: ['2xl', '3xl'],
      fontWeight: 'bold',
      lineHeight: 1.2,
      direction: direction,
    },
    h2: {
      fontSize: ['xl', '2xl'],
      fontWeight: 'bold',
      lineHeight: 1.2,
      direction: direction,
    },
    h3: {
      fontSize: ['lg', 'xl'],
      fontWeight: 'bold',
      lineHeight: 1.2,
      direction: direction,
    },
    body: {
      fontSize: 'md',
      lineHeight: 1.5,
      direction: direction,
    },
    caption: {
      fontSize: 'sm',
      color: 'text.secondary',
      direction: direction,
    },
  },
  
  // Layer styles for consistent card designs
  layerStyles: {
    card: {
      bg: 'white',
      borderRadius: 'lg',
      boxShadow: 'md',
      p: 6,
    },
    section: {
      bg: 'background.muted',
      borderRadius: 'md',
      p: 4,
    },
  },
});

export default theme;
